"use client"

import type { MouseEvent } from "react"

import Image from "next/image"
import { useTranslations } from "next-intl"

import { Button, CheckIcon, Grid, Heading, PressableRow, Row, Stack, Text } from "@/components/ui"
import type { ReferralShareActions } from "@/features/referrals/hooks/use-referral-share-actions"
import { tv } from "@/lib/utils/tv"

interface ReferralCodeRowProps {
  code: string
  copied: boolean
  pending?: boolean
  failed?: boolean
  onCopy: () => void
}

export const ReferralCodeRow = ({ code, copied, pending = false, failed = false, onCopy }: ReferralCodeRowProps) => {
  const t = useTranslations("referrals")
  return (
    <Row gap="300" className="border-line-subtle bg-surface rounded-8x h-16 border pr-200 pl-400" data-testid="referral-code-chip">
      <Text as="span" variant="bodyLg" family="text" truncate className="min-w-0 flex-1 font-medium" data-testid="referral-code">
        {code}
      </Text>
      <Button
        size="sm"
        className="text-body-lg h-10 px-600"
        loading={pending}
        disabled={pending}
        iconLeft={copied ? <CheckIcon size={16} /> : undefined}
        onClick={onCopy}
        data-testid="referral-copy-code"
      >
        {copied ? t("copied") : failed ? t("retry") : t("copyCode")}
      </Button>
    </Row>
  )
}

const channel = tv({
  base: "focus-visible:ring-brand rounded-6x flex flex-col items-center gap-100 py-100 text-caption text-fg-title font-text",
  variants: { disabled: { true: "opacity-50" } },
})

interface ChannelLinkProps {
  href: string | null
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void
  icon: string
  label: string
  testId: string
}

const ChannelIcon = ({ src }: { src: string }) => <Image src={src} alt="" width={54} height={54} className="size-13.5" />

const ChannelLink = ({ href, onClick, icon, label, testId }: ChannelLinkProps) =>
  href ? (
    <a href={href} target="_blank" rel="noreferrer" onClick={onClick} className={channel()} data-testid={testId}>
      <ChannelIcon src={icon} />
      {label}
    </a>
  ) : (
    <PressableRow disabled className={channel({ disabled: true })}>
      <ChannelIcon src={icon} />
      {label}
    </PressableRow>
  )

interface ReferralChannelsProps {
  actions: ReferralShareActions
  instagramOpen: boolean
  onInstagram: () => void
}

export const ReferralChannels = ({ actions, instagramOpen, onInstagram }: ReferralChannelsProps) => {
  const t = useTranslations("referrals")
  return (
    <Row gap="0" justify="between" className="border-line rounded-8x border p-400" role="group" aria-label={t("dialog.channelsLabel")}>
      <ChannelLink {...actions.whatsapp} icon="/images/referrals/social-whatsapp.svg" label={t("whatsapp")} testId="referral-channel-whatsapp" />
      <PressableRow onClick={onInstagram} disabled={!actions.ready} aria-expanded={instagramOpen} className={channel()} data-testid="referral-channel-instagram">
        <ChannelIcon src="/images/referrals/social-instagram.svg" />
        {t("dialog.instagram")}
      </PressableRow>
      <ChannelLink {...actions.facebook} icon="/images/referrals/social-facebook.svg" label={t("dialog.facebook")} testId="referral-channel-facebook" />
      <PressableRow onClick={actions.copyLink} disabled={!actions.ready} className={channel()} data-testid="referral-channel-url">
        <ChannelIcon src="/images/referrals/social-url.svg" />
        {actions.feedback === "link" ? t("linkCopied") : t("dialog.url")}
      </PressableRow>
    </Row>
  )
}

const instagramOption = tv({ base: "border-line-subtle rounded-8x text-body text-fg-brand font-text flex-col justify-end gap-400 border px-300 pt-500 pb-400 text-center" })

export const ReferralInstagramChooser = ({ actions, body }: { actions: ReferralShareActions; body: string }) => {
  const t = useTranslations("referrals")
  return (
    <Stack gap="400" className="referral-rise" data-testid="referral-instagram">
      <Stack gap="200">
        <Heading as="h3" variant="sm" tone="strong" className="font-semibold">
          {t("dialog.instagramTitle")}
        </Heading>
        <Text as="p" variant="body" family="text">
          {body}
        </Text>
      </Stack>
      <Grid cols={2} gap="300">
        <PressableRow onClick={actions.shareToStory} disabled={!actions.ready} className={instagramOption()} data-testid="referral-instagram-story">
          <span className="bg-story-referral rounded-4x h-17 w-9" aria-hidden />
          {t("dialog.instagramStory")}
        </PressableRow>
        <PressableRow onClick={actions.shareDirect} disabled={!actions.ready} className={instagramOption()} data-testid="referral-instagram-direct">
          <Image src="/images/referrals/instagram-dm.svg" alt="" width={56} height={36} />
          {t("dialog.instagramDirect")}
        </PressableRow>
      </Grid>
      {actions.feedback === "story" && (
        <Text as="p" variant="bodySm" family="text" tone="brand" className="text-center">
          {t("dialog.storyReady")}
        </Text>
      )}
    </Stack>
  )
}

interface ReferralShareStatusProps {
  actions: ReferralShareActions
  error?: string | null
}

export const ReferralShareStatus = ({ actions, error = null }: ReferralShareStatusProps) => {
  const t = useTranslations("referrals")
  const { feedback } = actions
  const message = error ?? (feedback === "failed" ? t("copyFailed") : null)
  return (
    <>
      <span role="status" aria-live="polite" className="sr-only">
        {feedback === "code" ? t("copied") : feedback === "link" ? t("linkCopied") : feedback === "story" ? t("dialog.storyReady") : ""}
      </span>
      {message && (
        <Text as="p" variant="bodySm" family="text" tone="danger" role="alert" className="text-center" data-testid="referral-share-error">
          {message}
        </Text>
      )}
    </>
  )
}
